import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Typography, Paper, Grid, Tab, TextField, MenuItem, 
  CircularProgress, Card, CardContent, Divider, Chip, Autocomplete,
  FormControlLabel, Radio, RadioGroup, Checkbox, Tooltip
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// Interfaces
interface AsistenciaData { name: string; Nuevos: number; Sucesivos: number; Total: number; }
interface FinanzasData { name: string; Consultas: number; Procedimientos: number; Total: number; }

const StatisticsPage = () => {
  const [tabValue, setTabValue] = useState('1');
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState<any>(null);
  
  // --- FILTROS DE TIEMPO ---
  const [startDate, setStartDate] = useState(dayjs().subtract(6, 'month').format('YYYY-MM-01'));
  const [endDate, setEndDate] = useState(dayjs().add(1, 'day').format('YYYY-MM-DD')); // +1 día para evitar lag de zona horaria
  const [groupBy, setGroupBy] = useState<'day' | 'month'>('month');

  // --- FILTROS EXCLUSIVOS ---
  const [filterMode, setFilterMode] = useState<'total' | 'specialist' | 'specialty'>('total');
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [selectedSpec, setSelectedSpec] = useState<string | null>(null);

  // --- FILTROS GEOGRÁFICOS ---
  const [showMun, setShowMun] = useState(false);
  const [selectedStateForMun, setSelectedStateForMun] = useState<any>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/stats/dashboard?start=${startDate}&end=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setRawData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, [startDate, endDate]);

  // --- LÓGICA DE FILTRADO DINÁMICO ---
  const filteredData = useMemo(() => {
    const evosBase = rawData?.evolutions || [];
    const procsBase = rawData?.procedures || [];

    let evos = [...evosBase];
    let procs = [...procsBase];

    if (filterMode === 'specialist' && selectedDoc) {
      evos = evos.filter((e: any) => e.doctorId === selectedDoc.id);
      procs = procs.filter((p: any) => p.doctorId === selectedDoc.id);
    } else if (filterMode === 'specialty' && selectedSpec) {
      const normalize = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
      const target = normalize(selectedSpec);
      evos = evos.filter((e: any) => e.specialty && normalize(e.specialty) === target);
      procs = procs.filter((p: any) => p.doctor?.specialty && normalize(p.doctor.specialty) === target);
    }

    return { evos, procs };
  }, [rawData, filterMode, selectedDoc, selectedSpec]);

  const dynamicTitle = useMemo(() => {
    if (filterMode === 'total') return "Estadística General del Centro";
    if (filterMode === 'specialist' && selectedDoc) return `Estadística: ${selectedDoc.prefix || ''} ${selectedDoc.name} ${selectedDoc.lastname}`;
    if (filterMode === 'specialty' && selectedSpec) return `Estadística Especialidad: ${selectedSpec}`;
    return "Inteligencia de Datos";
  }, [filterMode, selectedDoc, selectedSpec]);

  // --- PROCESAMIENTO ASISTENCIA ---
  const asistenciaData = useMemo<AsistenciaData[]>(() => {
    const groups: Record<string, AsistenciaData> = {};
    filteredData.evos.forEach((evo: any) => {
      let key = groupBy === 'month' ? dayjs(evo.date).format('MMM YYYY') : dayjs(evo.date).format('DD/MM');
      if (!groups[key]) groups[key] = { name: key, Nuevos: 0, Sucesivos: 0, Total: 0 };
      if (evo.visitType === 'NUEVO') groups[key].Nuevos++;
      else groups[key].Sucesivos++;
      groups[key].Total++;
    });
    return Object.values(groups);
  }, [filteredData.evos, groupBy]);

  // --- PROCESAMIENTO FINANZAS ---
  const finanzasData = useMemo<FinanzasData[]>(() => {
    const groups: Record<string, FinanzasData> = {};
    const processEntry = (date: any, amount: any, category: string) => {
      let key = groupBy === 'month' ? dayjs(date).format('MMM YYYY') : dayjs(date).format('DD/MM');
      if (!groups[key]) groups[key] = { name: key, Consultas: 0, Procedimientos: 0, Total: 0 };
      const val = parseFloat(amount || 0);
      if (category === 'PROCEDIMIENTO') groups[key].Procedimientos += val; else groups[key].Consultas += val;
      groups[key].Total += val;
    };
    filteredData.evos.forEach((e: any) => processEntry(e.date, e.amountPaid, e.servicePrice?.category || 'CONSULTA'));
    filteredData.procs.forEach((p: any) => processEntry(p.createdAt, p.amountPaid, 'PROCEDIMIENTO'));
    return Object.values(groups);
  }, [filteredData, groupBy]);

  // --- LÓGICA GEOGRÁFICA (CORREGIDA) ---
  const geoStats = useMemo(() => {
    // Extraer pacientes de los datos ya filtrados
    const patientsFromEvos = filteredData.evos.map((e: any) => e.patient).filter(Boolean);
    const patientsFromProcs = filteredData.procs.map((p: any) => p.patient).filter(Boolean);
    const allRelevantPatients = [...patientsFromEvos, ...patientsFromProcs];

    const statesMap: Record<string, number> = {};
    allRelevantPatients.forEach((p: any) => {
      const name = p.state?.name || 'No Registrado';
      statesMap[name] = (statesMap[name] || 0) + 1;
    });

    const munList: any[] = [];
    if (showMun && selectedStateForMun) {
        const munMap: Record<string, number> = {};
        allRelevantPatients
            .filter((p: any) => p.state?.id === selectedStateForMun.id)
            .forEach((p: any) => {
                const name = p.municipality?.name || 'No Registrado';
                munMap[name] = (munMap[name] || 0) + 1;
            });
        Object.entries(munMap).forEach(([name, count]) => munList.push({ name, Cantidad: count }));
    }

    return { 
        states: Object.entries(statesMap).map(([name, Cantidad]) => ({ name, Cantidad })).sort((a:any, b:any) => b.Cantidad - a.Cantidad),
        municipalities: munList.sort((a, b) => b.Cantidad - a.Cantidad)
    };
  }, [filteredData, showMun, selectedStateForMun]);

  if (loading && !rawData) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>📊 {dynamicTitle}</Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #ddd' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="Desde" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Hasta" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: 1, borderLeft: '2px solid #ccc' }}>
                <RadioGroup row value={filterMode} onChange={(e) => { setFilterMode(e.target.value as any); setSelectedDoc(null); setSelectedSpec(null); }}>
                    <FormControlLabel value="total" control={<Radio size="small" />} label={<Typography variant="caption" fontWeight="bold">TOTAL</Typography>} />
                    <FormControlLabel value="specialist" control={<Radio size="small" />} label={<Typography variant="caption" fontWeight="bold">ESPECIALISTA</Typography>} />
                    <FormControlLabel value="specialty" control={<Radio size="small" />} label={<Typography variant="caption" fontWeight="bold">ESPECIALIDAD</Typography>} />
                </RadioGroup>
                {filterMode === 'specialist' && (
                    <Autocomplete size="small" sx={{ width: 250 }} options={rawData?.specialists || []} getOptionLabel={(o) => `${o.prefix || ''} ${o.name} ${o.lastname}`} value={selectedDoc} onChange={(_, v) => setSelectedDoc(v)} renderInput={(params) => <TextField {...params} label="Médico" />} />
                )}
                {filterMode === 'specialty' && (
                    <Autocomplete size="small" sx={{ width: 250 }} options={rawData?.favoriteSpecialties || []} value={selectedSpec} onChange={(_, v) => setSelectedSpec(v)} renderInput={(params) => <TextField {...params} label="Especialidad" />} />
                )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '8px solid #607d8b', bgcolor: '#eceff1' }}>
                <Typography variant="overline" color="text.secondary">Pacientes Registrados</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{rawData?.totalPatientsCount || 0}</Typography>
            </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '8px solid #4caf50', bgcolor: '#f1f8e9' }}>
                <Typography variant="overline" color="text.secondary">Consultas (Periodo)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{asistenciaData.reduce((a, c) => a + c.Total, 0)}</Typography>
            </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '8px solid #2196f3', bgcolor: '#e3f2fd' }}>
                <Typography variant="overline" color="text.secondary">Nuevos (Periodo)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{asistenciaData.reduce((a, c) => a + c.Nuevos, 0)}</Typography>
            </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '8px solid #9c27b0', bgcolor: '#f3e5f5' }}>
                <Typography variant="overline" color="text.secondary">Ingresos (USD)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>${finanzasData.reduce((a, c) => a + c.Total, 0).toLocaleString()}</Typography>
            </Paper>
        </Grid>
      </Grid>

      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={(_, v) => setTabValue(v)}>
            <Tab label="Asistencia" value="1" />
            <Tab label="Geografía" value="2" />
            <Tab label="Ingresos" value="3" />
          </TabList>
        </Box>

        <TabPanel value="1">
            <Paper sx={{ p: 3, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={asistenciaData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" /> <YAxis /> <RechartsTooltip /> <Legend />
                        <Bar dataKey="Nuevos" fill="#4caf50" stackId="a" />
                        <Bar dataKey="Sucesivos" fill="#2196f3" stackId="a" />
                    </BarChart>
                </ResponsiveContainer>
            </Paper>
        </TabPanel>

        <TabPanel value="2">
            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, height: 450 }}>
                        <Typography variant="h6" gutterBottom>Distribución por Estados</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={geoStats.states} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={120} />
                                <RechartsTooltip />
                                <Bar dataKey="Cantidad" fill="#ed6c02" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6">Top Procedencia</Typography>
                                <FormControlLabel control={<Checkbox size="small" checked={showMun} onChange={(e) => setShowMun(e.target.checked)} />} label={<Typography variant="caption">Ver Municipios</Typography>} />
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            {!showMun ? (
                                geoStats.states.map((g: any, i: number) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{g.name}</Typography>
                                        <Chip label={g.Cantidad} size="small" color="primary" />
                                    </Box>
                                ))
                            ) : (
                                <Box>
                                    <Autocomplete size="small" sx={{ mb: 2 }} options={geoStats.states.map(s => ({ name: s.name, id: rawData?.patientsGeo?.find((p:any) => p.state?.name === s.name)?.state?.id }))} getOptionLabel={(o) => o.name} onChange={(_, v) => setSelectedStateForMun(v)} renderInput={(params) => <TextField {...params} label="Seleccionar Estado" />} />
                                    {selectedStateForMun && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>📍 Municipios de {selectedStateForMun.name}</Typography>
                                            <Box sx={{ maxHeight: 250, overflowY: 'auto', pr: 1 }}>
                                                {geoStats.municipalities.map((m: any, i: number) => (
                                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 0.5, borderBottom: '1px solid #eee' }}>
                                                        <Typography variant="body2">{m.name}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{m.Cantidad}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </TabPanel>

        <TabPanel value="3">
            <Paper sx={{ p: 3, height: 450 }}>
                <Typography variant="h6" gutterBottom>Ingresos: Consultas vs Procedimientos</Typography>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={finanzasData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" /> <YAxis /> <RechartsTooltip formatter={(v) => `$${v}`} /> <Legend />
                        <Area type="monotone" dataKey="Consultas" stackId="1" stroke="#2196f3" fill="#2196f3" name="Ingresos Consultas" />
                        <Area type="monotone" dataKey="Procedimientos" stackId="1" stroke="#4caf50" fill="#4caf50" name="Ingresos Procedimientos" />
                        <Area type="monotone" dataKey="Total" stroke="#9c27b0" fill="transparent" strokeWidth={3} name="Ingreso Total" />
                    </AreaChart>
                </ResponsiveContainer>
            </Paper>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default StatisticsPage;